import React from 'react';
import { Building2, Calendar, Users, Info } from 'lucide-react';

const proyectoTipos = [
  { id: 'residencial', label: 'Residencial' },
  { id: 'comercial', label: 'Comercial' },
  { id: 'mixto', label: 'Mixto' }
];

const proyectoEstados = [
  { id: 'planos', label: 'En Planos' },
  { id: 'lanzamiento', label: 'Lanzamiento' },
  { id: 'construccion', label: 'En Construcción' },
  { id: 'terminado', label: 'Terminado' }
];

const mesesOptions = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const currentYear = new Date().getFullYear();
const yearsOptions = Array.from({ length: 10 }, (_, i) => currentYear + i);

const InfProyPublish = ({ 
  formData = {}, 
  handleInputChange, 
  errors = {} 
}) => {
  const safeFormData = {
    nombre_proyecto: formData?.nombre_proyecto || '',
    descripcion_proyecto: formData?.descripcion_proyecto || '',
    tipo_proyecto: formData?.tipo_proyecto || '',
    estado_proyecto: formData?.estado_proyecto || '',
    mes_entrega: formData?.mes_entrega || '',
    anio_entrega: formData?.anio_entrega || '',
    total_unidades: formData?.total_unidades || '',
    ...formData
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <Building2 className="w-16 h-16 mx-auto text-blue-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Información del Proyecto
        </h2>
        <p className="text-gray-600">
          Proporciona los detalles básicos de tu proyecto inmobiliario
        </p>
      </div>

      {/* Información Básica */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-500" />
          Datos Principales
        </h3>
        
        <div className="space-y-4">
          {/* Nombre del Proyecto */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Nombre del Proyecto*
            </label>
            <input
              type="text"
              name="nombre_proyecto"
              value={safeFormData.nombre_proyecto}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ej: Residencial Las Palmeras"
              maxLength={60}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {safeFormData.nombre_proyecto?.length || 0}/60 caracteres
              </span>
              {errors.nombre_proyecto && (
                <span className="text-red-500 text-xs">{errors.nombre_proyecto}</span>
              )}
            </div>
          </div>

          {/* Tipo y Estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Tipo de Proyecto*
              </label>
              <select
                name="tipo_proyecto"
                value={safeFormData.tipo_proyecto}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Seleccione el tipo</option>
                {proyectoTipos.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>{tipo.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Estado del Proyecto*
              </label>
              <select
                name="estado_proyecto"
                value={safeFormData.estado_proyecto}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Seleccione el estado</option>
                {proyectoEstados.map(estado => (
                  <option key={estado.id} value={estado.id}>{estado.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Descripción del Proyecto*
            </label>
            <textarea
              name="descripcion_proyecto"
              value={safeFormData.descripcion_proyecto}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
              placeholder="Describe el concepto del proyecto, características principales, ubicación y beneficios que ofrece..."
              maxLength={700}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {safeFormData.descripcion_proyecto?.length || 0}/700 caracteres
              </span>
              {safeFormData.descripcion_proyecto?.length > 0 && safeFormData.descripcion_proyecto?.length < 100 && (
                <span className="text-amber-500 text-xs">
                  Se recomienda al menos 100 caracteres
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cronograma y Capacidad */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-indigo-500" />
          Cronograma y Capacidad
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Mes de Entrega */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Mes de Entrega*
            </label>
            <select
              name="mes_entrega"
              value={safeFormData.mes_entrega}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
            >
              <option value="">Seleccione mes</option>
              {mesesOptions.map((mes, index) => (
                <option key={index} value={mes}>{mes}</option>
              ))}
            </select>
          </div>
          
          {/* Año de Entrega */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Año de Entrega*
            </label>
            <select
              name="anio_entrega"
              value={safeFormData.anio_entrega}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
            >
              <option value="">Seleccione año</option>
              {yearsOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          {/* Total de Unidades */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Total de Unidades*
            </label>
            <input
              type="number"
              name="total_unidades"
              value={safeFormData.total_unidades}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="Ej: 24"
              min="1"
            />
          </div>
        </div>
        
        {/* Info adicional */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">Información importante</h4>
              <p className="text-sm text-blue-700 mt-1">
                Asegúrate de que la fecha de entrega sea realista según el estado actual del proyecto. 
                Esta información será visible para los potenciales compradores.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfProyPublish;